import { useEffect, useRef, useState } from 'react'
import type { Design_ } from 'src/api/db/apiDesigns'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  ChevronRight,
  Copy,
  Ellipsis,
  Images,
  Loader2,
  Palette,
  ShoppingCart,
  Sparkles,
  Stamp,
  Trash2,
} from 'lucide-react'
import type { ProductVariantColor_ } from 'src/api/db/apiProductVariantColor'
import { buildAutoMockupPayload } from 'src/api/server/buildGenerateMockupsPayload'
import { useGenerateMockups } from 'src/api/server/useGenerateMockups'
import { DesignImage } from 'src/shared/components/Design'
import { MockupGeneratingOverlay } from 'src/shared/components/MockupGeneratingOverlay'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'
import { EmptyState } from 'src/shared/components/EmptyState'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu'
import { paths } from 'src/routes/paths'
import { Image } from 'src/shared/components/ui'
import { Badge } from 'src/shared/components/ui/badge'
import { Tooltip } from 'src/shared/components/ui/tooltip'
import { cn } from 'src/shared/components/ui/utils'
import { getVariantColorImageUrl } from 'src/shared/utils/quoteItemThumbnail'
import {
  ChangeDecorationDialog,
  ChangeDecorationMenuItems,
} from 'src/pages/_myAccount/EditDecoration/components/ChangeDecorationDialog'
import { useLineAlternatives } from '../../Alternatives/useLineAlternatives'
import { MockupGalleryDialog } from './MockupGalleryDialog'

export const DecorationCard = ({
  design,
  productName,
  decorationName,
  quoteItemId,
  quoteSubItemId,
  price,
  fallbackImage,
  mockup_urls,
  public_id,
  isReadOnly,
  fullBleed = false,
  compact = false,
  onEditClick,
  onDuplicate,
  onDelete,
  variantColors,
  chosenVariantColorId,
}: {
  design: Design_ | null | undefined
  productName: string
  decorationName: string
  quoteItemId: number
  quoteSubItemId: number
  price?: number
  fallbackImage?: string
  mockup_urls?: string[]
  public_id?: string
  isReadOnly?: boolean
  fullBleed?: boolean
  compact?: boolean
  onEditClick?: () => void
  onDuplicate?: () => void
  onDelete?: () => void
  variantColors?: ProductVariantColor_[]
  chosenVariantColorId?: number
}) => {
  const navigate = useNavigate()
  const { t } = useLocalization()
  const currency = useOrganizationCurrency()
  const generateMockups = useGenerateMockups()
  const [selectedImageIndex, setSelectedImageIndex] = useState(() =>
    mockup_urls && mockup_urls.length > 0 ? 1 : 0
  )
  const [galleryOpen, setGalleryOpen] = useState(false)
  // Visual mockup only — not persisted anywhere yet.
  const [inQuote, setInQuote] = useState(true)
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    null
  )
  const [changeDecorationOpen, setChangeDecorationOpen] = useState(false)

  const hasMockups = Boolean(mockup_urls && mockup_urls.length > 0)
  const mockupCount = mockup_urls?.length ?? 0
  const alternatives = useLineAlternatives({ quoteItemId, isReadOnly })
  const hasActionsMenu = Boolean(
    onEditClick || onDuplicate || onDelete || alternatives.enabled
  )

  const activeVariantId =
    selectedVariantId ?? chosenVariantColorId ?? variantColors?.[0]?.id
  const variantImageOf = (variant: ProductVariantColor_) =>
    getVariantColorImageUrl(variant)
  // When the user previews another color, show that variant's product photo.
  const selectedVariant =
    selectedVariantId != null && selectedVariantId !== chosenVariantColorId
      ? variantColors?.find((variant) => variant.id === selectedVariantId)
      : undefined
  const variantOverrideImage = selectedVariant
    ? variantImageOf(selectedVariant)
    : undefined
  const chosenVariantImage =
    chosenVariantColorId != null
      ? getVariantColorImageUrl(
          variantColors?.find((variant) => variant.id === chosenVariantColorId)
        )
      : null

  const activeProductImageUrl =
    variantOverrideImage ??
    design?.image_url ??
    fallbackImage ??
    chosenVariantImage ??
    null

  const activeVariantColor =
    variantColors?.find((variant) => variant.id === activeVariantId)?.color ??
    null

  const autoPayloadPreview = buildAutoMockupPayload({
    quote_sub_item_id: quoteSubItemId,
    quote_item_id: quoteItemId,
    product_name: productName,
    decoration_name: decorationName,
    product_image_url: activeProductImageUrl,
    logo_url: design?.design_url ?? null,
    product_color: activeVariantColor,
    design,
  })

  const handleGenerateMockup = ({
    logoUrl,
    customPrompt,
  }: {
    logoUrl: string
    customPrompt: string
  }) => {
    const { payload } = buildAutoMockupPayload({
      quote_sub_item_id: quoteSubItemId,
      quote_item_id: quoteItemId,
      product_name: productName,
      decoration_name: decorationName,
      product_image_url: activeProductImageUrl,
      logo_url: logoUrl,
      product_color: activeVariantColor,
      custom_prompt: customPrompt,
      design,
    })
    if (!payload) return
    generateMockups.mutate(payload)
  }

  const prevMockupCountRef = useRef(mockup_urls?.length ?? 0)

  useEffect(() => {
    const count = mockup_urls?.length ?? 0
    if (count > prevMockupCountRef.current && count > 0) {
      // Regeneration appends — jump to the first newly added mockup.
      setSelectedImageIndex(prevMockupCountRef.current + 1)
    } else if (count === 0) {
      setSelectedImageIndex(0)
    } else {
      setSelectedImageIndex((prev) => (prev > count ? count : prev))
    }
    prevMockupCountRef.current = count
  }, [mockup_urls])

  const openGallery = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    setGalleryOpen(true)
  }

  return (
    <Column
      className={cn(
        'relative bg-white shadow-lg',
        compact
          ? 'w-[240px] overflow-hidden rounded-xl border border-gray-200'
          : fullBleed
            ? 'rounded-none border-0 bg-gray-50'
            : 'rounded-lg border border-gray-200 bg-gray-50'
      )}
    >
      <div
        className={cn(
          'relative w-full overflow-hidden bg-white',
          'aspect-square',
          !fullBleed && !compact && 'rounded-t-lg'
        )}
      >
        {selectedImageIndex > 0 && (
          <Badge
            className={cn(
              'absolute z-10',
              compact ? 'right-1 top-1 text-[10px]' : 'right-2 top-2'
            )}
          >
            {t('Mockup', 'Mockup')}
          </Badge>
        )}
        {selectedImageIndex === 0 ? (
          variantOverrideImage ? (
            <Image
              src={variantOverrideImage}
              alt={selectedVariant?.color || productName}
              className="h-full w-full object-cover"
            />
          ) : design ? (
            <DesignImage
              logo_url={design?.design_url || null}
              image_url={activeProductImageUrl || ''}
              design={design}
              containerClassName="absolute inset-0 h-full w-full"
              imageClassName="rounded-none object-contain"
            />
          ) : (
            <Image
              src={activeProductImageUrl || ''}
              alt={decorationName}
              className="h-full w-full object-cover"
            />
          )
        ) : (
          <Image
            src={mockup_urls?.[selectedImageIndex - 1] || ''}
            alt={t(
              `Mockup ${selectedImageIndex}`,
              `Mockup ${selectedImageIndex}`
            )}
            className="h-full w-full bg-white object-contain"
          />
        )}
        {compact && generateMockups.isPending && (
          <MockupGeneratingOverlay
            compact
            loadingText={t('Generating mockup...', 'Genererar mockup...')}
          />
        )}
      </div>

      {compact ? (
        <Column className="gap-1.5 bg-gradient-to-t from-white to-transparent p-2">
          <Column className="min-w-0 gap-0.5">
            <p className="truncate text-[11px] font-medium leading-tight text-gray-900">
              {productName}
            </p>
            <Row className="items-center gap-1.5">
              {price != null && (
                <p className="shrink-0 text-[11px] font-medium text-gray-900">
                  {price.toLocaleString()} {currency}
                </p>
              )}
              <Row className="min-w-0 items-center gap-1">
                <Stamp className="h-2.5 w-2.5 shrink-0 text-gray-400" />
                <p className="truncate text-[10px] text-gray-500">
                  {t(decorationName)}
                </p>
              </Row>
            </Row>
            {alternatives.badge}
          </Column>

          {variantColors && variantColors.length > 1 && (
            <Row
              className="items-center gap-1 overflow-x-auto pb-0.5"
              style={{ scrollbarWidth: 'none' }}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {variantColors.map((variant) => {
                const isSelected = variant.id === activeVariantId
                return (
                  <Tooltip
                    key={variant.id}
                    content={variant.color || t('Color', 'Färg')}
                    side="top"
                    delayDuration={0}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedVariantId(variant.id)}
                      className={cn(
                        'h-6 w-6 shrink-0 overflow-hidden rounded border bg-white transition-colors',
                        isSelected
                          ? 'border-blue-500 ring-1 ring-blue-500/30'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <Image
                        src={variantImageOf(variant) || ''}
                        alt={variant.color || ''}
                        className="h-full w-full object-contain p-0.5"
                      />
                    </button>
                  </Tooltip>
                )
              })}
            </Row>
          )}

          <Row className="items-center gap-1.5" onPointerDown={(e) => e.stopPropagation()}>
              <Button
                variant="secondary"
                size="sm"
                className={cn(
                  'h-7 shrink-0',
                  hasMockups ? 'w-7 p-0' : 'gap-1.5 px-2',
                  inQuote
                    ? 'translate-y-px !shadow-[var(--skeuo-pressed)] text-gray-900'
                    : 'text-gray-400 hover:text-gray-600'
                )}
                aria-pressed={inQuote}
                aria-label={
                  inQuote
                    ? t('In order', 'I ordern')
                    : t('Add to order', 'Lägg till i ordern')
                }
                onClick={(e) => {
                  e.stopPropagation()
                  setInQuote((prev) => !prev)
                }}
              >
                <span className="relative flex items-center justify-center">
                  <ShoppingCart className="h-3 w-3" />
                  {!inQuote && (
                    <span className="absolute h-px w-4 -rotate-45 rounded-full bg-current" />
                  )}
                </span>
                {!hasMockups && (
                  <span className="text-[11px]">
                    {inQuote
                      ? t('In order', 'I ordern')
                      : t('Add to order', 'Lägg till i ordern')}
                  </span>
                )}
              </Button>
            {hasActionsMenu && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-7 w-7 shrink-0 p-0"
                    aria-label={t('Actions', 'Åtgärder')}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Ellipsis className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="min-w-[168px]"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  {onEditClick && (
                    <ChangeDecorationMenuItems
                      onManualDesign={onEditClick}
                      onChangeDecoration={() => setChangeDecorationOpen(true)}
                    />
                  )}
                  {onEditClick && !hasMockups && (
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        openGallery()
                      }}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      {t('Generate mockups', 'Generera mockups')}
                    </DropdownMenuItem>
                  )}
                  {onDuplicate && (
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDuplicate()
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {t('Duplicate', 'Duplicera')}
                    </DropdownMenuItem>
                  )}
                  {alternatives.menuItems}
                  {onDelete && (
                    <>
                      {(onEditClick || onDuplicate) && (
                        <DropdownMenuSeparator />
                      )}
                      <DropdownMenuItem
                        className="gap-2 text-red-600 focus:bg-red-50 focus:text-red-600"
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete()
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {t('Delete', 'Ta bort')}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {hasMockups && (
              <Tooltip
                content={t('View mockups', 'Visa mockups')}
                side="top"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={openGallery}
                  className="group h-7 min-w-0 w-auto flex-1 justify-end gap-1.5 px-1.5"
                >
                  {generateMockups.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                  ) : (
                    <span className="flex shrink-0 items-center -space-x-1.5">
                      {mockup_urls?.slice(0, 3).map((url, i) => (
                        <span
                          key={url}
                          className={cn(
                            'block h-5 w-5 overflow-hidden rounded-[5px] border border-gray-200 bg-white shadow-sm transition-transform duration-200',
                            [
                              '-rotate-6 group-hover:-rotate-12',
                              'rotate-2 group-hover:rotate-6 z-10',
                              '-rotate-3 group-hover:rotate-12',
                            ][i]
                          )}
                        >
                          <Image
                            src={url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        </span>
                      ))}
                    </span>
                  )}
                  <span className="text-[11px] font-medium text-gray-900">
                    {t('Mockups', 'Mockups')}
                  </span>
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                </Button>
              </Tooltip>
            )}
          </Row>

          <MockupGalleryDialog
            open={galleryOpen}
            onOpenChange={setGalleryOpen}
            mockupUrls={mockup_urls ?? []}
            productName={productName}
            quoteSubItemId={quoteSubItemId}
            designUrl={design?.design_url}
            canGenerate={!!activeProductImageUrl}
            disabledReason={autoPayloadPreview.reason ?? undefined}
            isGenerating={generateMockups.isPending}
            onGenerate={handleGenerateMockup}
          />
        </Column>
      ) : (
        <Row
          className={cn(
            'items-end bg-gradient-to-t from-white to-transparent',
            'rounded-b-lg p-3',
            fullBleed && 'rounded-none'
          )}
        >
          <Column className="min-w-0 flex-1 gap-0.5">
            <p className="text-sm text-gray-900">{productName}</p>

            <Row className="items-center gap-1.5">
              <Stamp className="h-3 w-3 shrink-0 text-gray-400" />
              <p className="text-sm text-gray-500">{t(decorationName)}</p>
            </Row>

            {hasMockups && (
              <Row className="mt-2 flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setSelectedImageIndex(0)}
                  className={cn(
                    'h-10 w-10 shrink-0 overflow-hidden rounded border-2 transition-colors',
                    selectedImageIndex === 0
                      ? 'border-blue-500 ring-2 ring-blue-500/30'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  {design ? (
                    <DesignImage
                      logo_url={design?.design_url || null}
                      image_url={activeProductImageUrl || ''}
                      design={design}
                      containerClassName="h-full w-full rounded-none"
                    />
                  ) : (
                    <Image
                      src={activeProductImageUrl || ''}
                      alt={decorationName}
                      className="h-full w-full object-cover"
                    />
                  )}
                </button>

                {mockup_urls?.map((url, i) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setSelectedImageIndex(i + 1)}
                    className={cn(
                      'h-10 w-10 shrink-0 overflow-hidden rounded border-2 transition-colors',
                      selectedImageIndex === i + 1
                        ? 'border-blue-500 ring-2 ring-blue-500/30'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <Image
                      src={url}
                      alt={t(`Mockup ${i + 1}`, `Mockup ${i + 1}`)}
                      className="h-full w-full bg-white object-contain"
                    />
                  </button>
                ))}
              </Row>
            )}
          </Column>

          <Row className="ml-auto mt-2 shrink-0 items-center gap-1">
            {!isReadOnly && (
              <Button variant="ghost" size="sm" onClick={openGallery}>
                {generateMockups.isPending ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : hasMockups ? (
                  <Images className="mr-1.5 h-3.5 w-3.5" />
                ) : (
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                )}
                {t('Mockups', 'Mockups')}
                {hasMockups && (
                  <span className="ml-1 text-muted-foreground">
                    {mockupCount}
                  </span>
                )}
              </Button>
            )}
            {!isReadOnly && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setChangeDecorationOpen(true)}
              >
                {t('Change decoration', 'Byt dekoration')}
              </Button>
            )}
            {!isReadOnly && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate(
                    `${paths.requests}/${public_id}/order_items/${quoteItemId}/sub_item/${quoteSubItemId}`
                  )
                }
              >
                {t('Manual design', 'Manuell design')}
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            )}
          </Row>

          <MockupGalleryDialog
            open={galleryOpen}
            onOpenChange={setGalleryOpen}
            mockupUrls={mockup_urls ?? []}
            productName={productName}
            quoteSubItemId={quoteSubItemId}
            designUrl={design?.design_url}
            canGenerate={!!activeProductImageUrl}
            disabledReason={autoPayloadPreview.reason ?? undefined}
            isGenerating={generateMockups.isPending}
            onGenerate={isReadOnly ? undefined : handleGenerateMockup}
          />
        </Row>
      )}

      <ChangeDecorationDialog
        open={changeDecorationOpen}
        onOpenChange={setChangeDecorationOpen}
        quoteItemId={quoteItemId}
        quoteSubItemId={quoteSubItemId}
      />
      {alternatives.dialog}
    </Column>
  )
}

export const DecorationsCard = ({
  allDesigns,
  public_id,
  isReadOnly = false,
  fullBleed = false,
}) => {
  const { t } = useLocalization()

  if (allDesigns?.length === 0) {
    return (
      <EmptyState
        icon={<Palette className="w-9 h-9" />}
        title={t('No decorations yet', 'Inga dekorationer ännu')}
        description={t(
          'Add decorations to products to see them here.',
          'Lägg till dekorationer på produkter för att se dem här.'
        )}
      />
    )
  }

  return (
    <div
      className={cn(
        fullBleed
          ? 'grid grid-cols-2 gap-px bg-gray-200 md:grid-cols-3 xl:grid-cols-4'
          : 'grid grid-cols-1 gap-4 md:grid-cols-2'
      )}
    >
      {allDesigns?.map(
        ({
          design,
          productName,
          decorationName,
          quoteItemId,
          quoteSubItemId,
          fallbackImage,
          mockup_urls,
        }) => (
          <DecorationCard
            key={quoteSubItemId}
            design={design}
            productName={productName}
            decorationName={decorationName}
            quoteItemId={quoteItemId}
            quoteSubItemId={quoteSubItemId}
            fallbackImage={fallbackImage}
            mockup_urls={mockup_urls}
            public_id={public_id}
            isReadOnly={isReadOnly}
            fullBleed={fullBleed}
          />
        )
      )}
    </div>
  )
}
